export type TemplateNode = {
  component: string
  body?: TemplateNode[]
  id?: string
  name?: string
  icon?: string
  propertiesComponent?: string
  properties?: Record<string, unknown>
  text?: string
  slot?: string
}

export type TemplateJson = TemplateNode | TemplateNode[] | Record<string, TemplateNode>

type RawNode = Partial<TemplateNode> & {
  body?: unknown
  properties?: unknown
  text?: unknown
  slot?: unknown
  icon?: unknown
  name?: unknown
}

type FlatComponentNode = {
  id?: unknown
  name?: unknown
  component?: unknown
  properties?: unknown
  body?: unknown
  slot?: unknown
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isTemplateNode = (value: unknown): value is TemplateNode =>
  isObject(value) && typeof value.component === 'string'

const isFlatComponentNode = (value: unknown): value is FlatComponentNode => {
  if (!isObject(value)) return false
  return typeof value.name === 'string' || typeof value.component === 'string'
}

const normalizeFlatComponentNode = (node: unknown): TemplateNode | null => {
  if (!isFlatComponentNode(node)) return null
  const component = typeof node.component === 'string' ? node.component : typeof node.name === 'string' ? node.name : ''
  if (!component) return null
  const children = normalizeChildren(node.body)

  return {
    component,
    body: children.length ? children : undefined,
    id: typeof node.id === 'string' ? node.id : undefined,
    name: typeof node.name === 'string' ? node.name : undefined,
    properties: isObject(node.properties) ? (node.properties as Record<string, unknown>) : undefined,
    slot: typeof node.slot === 'string' ? node.slot : undefined,
  }
}

const normalizeFlatComponents = (components: unknown): TemplateNode[] => {
  if (!Array.isArray(components)) return []
  return components
    .map((item) => normalizeFlatComponentNode(item))
    .filter((node): node is TemplateNode => !!node)
}

const pickFlatComponents = (input: unknown): TemplateNode[] | null => {
  if (!isObject(input) || !Array.isArray(input.components)) return null
  const nodes = normalizeFlatComponents(input.components)
  return nodes.length ? nodes : null
}

const normalizeChildren = (body: unknown): TemplateNode[] => {
  if (!Array.isArray(body)) return []
  return body
    .map((child) => normalizeNode(child) || normalizeFlatComponentNode(child))
    .filter((child): child is TemplateNode => !!child)
}

const normalizeNode = (node: unknown): TemplateNode | null => {
  if (!isObject(node)) return null
  const raw = node as RawNode
  const component = typeof raw.component === 'string' ? raw.component : ''
  if (!component) return null

  const children = normalizeChildren(raw.body)
  return {
    component,
    body: children.length ? children : undefined,
    id: raw.id,
    name: raw.name,
    icon: typeof raw.icon === 'string' ? raw.icon : undefined,
    propertiesComponent: raw.propertiesComponent,
    properties: isObject(raw.properties) ? (raw.properties as Record<string, unknown>) : undefined,
    text: typeof raw.text === 'string' ? raw.text : undefined,
    slot: typeof raw.slot === 'string' ? raw.slot : undefined,
  }
}

const wrapSectionNode = (sectionName: string, section: unknown): TemplateNode | null => {
  if (!isObject(section)) return null
  const raw = section as RawNode
  const children = normalizeChildren(raw.body)
  const rawComponent = typeof raw.component === 'string' ? raw.component : ''
  const shouldWrapChild = rawComponent && rawComponent !== sectionName

  if (shouldWrapChild) {
    const wrappedChild: TemplateNode = {
      component: rawComponent,
      body: children.length ? children : undefined,
      id: raw.id,
      name: raw.name,
      icon: typeof raw.icon === 'string' ? raw.icon : undefined,
      propertiesComponent: raw.propertiesComponent,
      properties: isObject(raw.properties) ? (raw.properties as Record<string, unknown>) : undefined,
      text: typeof raw.text === 'string' ? raw.text : undefined,
      slot: typeof raw.slot === 'string' ? raw.slot : undefined,
    }

    return {
      component: sectionName,
      body: [wrappedChild],
      name: raw.name,
    }
  }

  // New structure: top-level sections (HeaderTop/Content/FooterBottom) own body array
  return {
    component: sectionName,
    body: children.length ? children : undefined,
    id: raw.id,
    name: raw.name,
    icon: typeof raw.icon === 'string' ? raw.icon : undefined,
    propertiesComponent: raw.propertiesComponent,
    properties: isObject(raw.properties) ? (raw.properties as Record<string, unknown>) : undefined,
    text: typeof raw.text === 'string' ? raw.text : undefined,
    slot: typeof raw.slot === 'string' ? raw.slot : undefined,
  }
}

export const buildRenderTree = (input: TemplateJson): TemplateNode[] => {
  const flatNodes = pickFlatComponents(input)
  if (flatNodes) return flatNodes

  if (Array.isArray(input)) {
    return input
      .map((node) => normalizeNode(node) || normalizeFlatComponentNode(node))
      .filter((node): node is TemplateNode => !!node)
  }

  if (isTemplateNode(input)) {
    return [normalizeNode(input)].filter((node): node is TemplateNode => !!node)
  }

  if (isObject(input)) {
    return Object.entries(input)
      .map(([sectionName, section]) => wrapSectionNode(sectionName, section))
      .filter((node): node is TemplateNode => !!node)
  }

  return []
}

export const findNodeById = (input: TemplateJson, id: string): TemplateNode | null => {
  const nodes = buildRenderTree(input)

  const walk = (list: TemplateNode[]): TemplateNode | null => {
    for (const node of list) {
      if (node.id === id) return node
      if (node.body) {
        const found = walk(node.body)
        if (found) return found
      }
    }
    return null
  }

  return walk(nodes)
}

export const updateNodeProperties = (
  input: TemplateJson,
  id: string,
  patch: Record<string, unknown>,
): boolean => {
  const applyPatch = (node: Record<string, unknown>): boolean => {
    if (node.id === id) {
      const current = isObject(node.properties) ? node.properties : {}
      node.properties = { ...current, ...patch }
      return true
    }

    if (Array.isArray(node.body)) {
      for (const child of node.body) {
        if (isObject(child) && applyPatch(child)) return true
      }
    }

    return false
  }

  if (Array.isArray(input)) {
    return input.some((node) => isObject(node) && applyPatch(node))
  }

  if (isObject(input)) {
    if (isTemplateNode(input) && applyPatch(input)) return true
    return Object.values(input).some((section) => isObject(section) && applyPatch(section))
  }

  return false
}

export const removeNodeById = (input: TemplateJson, id: string): TemplateNode | null => {
  const removeFromList = (list: unknown[]): TemplateNode | null => {
    for (let index = 0; index < list.length; index += 1) {
      const item = list[index]
      if (!isObject(item)) continue
      if (item.id === id) {
        list.splice(index, 1)
        return item as TemplateNode
      }
      if (Array.isArray(item.body)) {
        const removed = removeFromList(item.body)
        if (removed) return removed
      }
    }
    return null
  }

  if (Array.isArray(input)) {
    return removeFromList(input)
  }

  if (isObject(input)) {
    if (isTemplateNode(input) && input.id === id) {
      return null
    }
    for (const section of Object.values(input)) {
      if (isObject(section) && Array.isArray(section.body)) {
        const removed = removeFromList(section.body)
        if (removed) return removed
      }
    }
  }

  return null
}

export const insertIntoSection = (
  input: TemplateJson,
  sectionName: 'HeaderTop' | 'Content' | 'FooterBottom',
  child: TemplateNode,
  position: 'start' | 'end',
): boolean => {
  if (!isObject(input)) return false
  const section = (input as Record<string, unknown>)[sectionName]
  if (!isObject(section)) return false
  const current = Array.isArray((section as { body?: unknown }).body)
    ? ((section as { body?: TemplateNode[] }).body as TemplateNode[])
    : []
  const next = position === 'start' ? [child, ...current] : [...current, child]
  ;(section as { body?: TemplateNode[] }).body = next
  return true
}

export const moveNode = (
  input: TemplateJson,
  id: string,
  targetParentId: string,
  position: 'start' | 'end',
): boolean => {
  const removed = removeNodeById(input, id)
  if (!removed) return false
  return insertChildNode(input, targetParentId, removed, position)
}

export const moveNodeToSection = (
  input: TemplateJson,
  id: string,
  sectionName: 'HeaderTop' | 'Content' | 'FooterBottom',
  position: 'start' | 'end',
): boolean => {
  const removed = removeNodeById(input, id)
  if (!removed) return false
  return insertIntoSection(input, sectionName, removed, position)
}

export const insertChildNode = (
  input: TemplateJson,
  parentId: string,
  child: TemplateNode,
  position: 'start' | 'end' = 'start',
): boolean => {
  const insertInto = (node: Record<string, unknown>): boolean => {
    if (node.id === parentId) {
      const current = Array.isArray(node.body) ? node.body : []
      node.body = position === 'start' ? [child, ...current] : [...current, child]
      return true
    }

    if (Array.isArray(node.body)) {
      for (const item of node.body) {
        if (isObject(item) && insertInto(item)) return true
      }
    }

    return false
  }

  if (Array.isArray(input)) {
    return input.some((node) => isObject(node) && insertInto(node))
  }

  if (isObject(input)) {
    if (isTemplateNode(input) && insertInto(input)) return true
    return Object.values(input).some((section) => isObject(section) && insertInto(section))
  }

  return false
}
